import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    FormControl,
    FormLabel,
    Input,
    Textarea,
    Button,
    useToast,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import api from "../../utils/axios";
import { languages, languageEnum } from "../../constants/languages";

const TRANSLATION_ENDPOINT = {
    menu: (id) => `/menu-translation/menu/${id}`,
    section: (id) => `/menu-translation/menu-section/${id}`,
    item: (id) => `/menu-translation/menu-item/${id}`,
};

// Only the section endpoint accepts/returns a "header" field.
const HAS_HEADER = { section: true };

const emptyValue = { name: "", header: "", description: "" };

const TranslationModal = ({ entityType, entity, onClose }) => {
    const [tabIndex, setTabIndex] = useState(0);
    const [values, setValues] = useState({});
    const [loadedLangs, setLoadedLangs] = useState({});
    const [loadingLang, setLoadingLang] = useState(null);
    const [saving, setSaving] = useState(false);
    const toast = useToast();

    const showHeader = !!HAS_HEADER[entityType];
    const endpoint = entity ? TRANSLATION_ENDPOINT[entityType](entity.id) : null;

    const fetchTranslation = async (langCode) => {
        if (loadedLangs[langCode] || !endpoint) return;

        setLoadingLang(langCode);
        try {
            const languageId = languageEnum[langCode];
            const response = await api.get(`${endpoint}?languageCode=${languageId}`);
            setValues((prev) => ({
                ...prev,
                [langCode]: {
                    name: response.data?.name || "",
                    header: response.data?.header || "",
                    description: response.data?.description || "",
                },
            }));
        } catch (error) {
            setValues((prev) => ({ ...prev, [langCode]: emptyValue }));
        } finally {
            setLoadedLangs((prev) => ({ ...prev, [langCode]: true }));
            setLoadingLang(null);
        }
    };

    useEffect(() => {
        if (entity) fetchTranslation(languages[0].code);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entity?.id, entityType]);

    if (!entity) return null;

    const handleTabChange = (index) => {
        setTabIndex(index);
        fetchTranslation(languages[index].code);
    };

    const updateField = (langCode, field, value) => {
        setValues((prev) => ({
            ...prev,
            [langCode]: { ...(prev[langCode] || emptyValue), [field]: value },
        }));
    };

    const activeLang = languages[tabIndex];
    const activeValue = values[activeLang.code] || emptyValue;

    const handleSave = async () => {
        if (!activeValue.name.trim()) {
            toast({
                title: "Error",
                description: "Name is required.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setSaving(true);
        try {
            const languageId = languageEnum[activeLang.code];
            const body = showHeader
                ? { name: activeValue.name.trim(), header: activeValue.header.trim(), description: activeValue.description.trim() }
                : { name: activeValue.name.trim(), description: activeValue.description.trim() };

            await api.post(`${endpoint}?languageCode=${languageId}`, body, {
                headers: { "Content-Type": "application/json" },
            });

            toast({
                title: "Success",
                description: "Translation saved successfully!",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: "Error",
                description: error.response?.data?.message || "Failed to save translation.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal isOpen={true} onClose={onClose} size="lg">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Translations - {entity.name}</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <Tabs
                        index={tabIndex}
                        onChange={handleTabChange}
                        colorScheme="brand"
                        isLazy
                    >
                        <TabList>
                            {languages.map((lang) => (
                                <Tab key={lang.code}>{lang.country}</Tab>
                            ))}
                        </TabList>
                        <TabPanels>
                            {languages.map((lang) => {
                                const value = values[lang.code] || emptyValue;
                                const isLoading = loadingLang === lang.code;
                                return (
                                    <TabPanel key={lang.code} px={0}>
                                        <FormControl isRequired mt={3}>
                                            <FormLabel>Name</FormLabel>
                                            <Input
                                                value={value.name}
                                                isDisabled={isLoading}
                                                onChange={(e) => updateField(lang.code, "name", e.target.value)}
                                            />
                                        </FormControl>
                                        {showHeader && (
                                            <FormControl mt={3}>
                                                <FormLabel>Header</FormLabel>
                                                <Input
                                                    value={value.header}
                                                    isDisabled={isLoading}
                                                    onChange={(e) => updateField(lang.code, "header", e.target.value)}
                                                />
                                            </FormControl>
                                        )}
                                        <FormControl mt={3}>
                                            <FormLabel>Description</FormLabel>
                                            <Textarea
                                                value={value.description}
                                                isDisabled={isLoading}
                                                onChange={(e) => updateField(lang.code, "description", e.target.value)}
                                                resize="vertical"
                                                minH="100px"
                                                maxH="250px"
                                            />
                                        </FormControl>
                                    </TabPanel>
                                );
                            })}
                        </TabPanels>
                    </Tabs>
                </ModalBody>
                <ModalFooter>
                    <Button onClick={onClose} mr={3} isDisabled={saving}>Cancel</Button>
                    <Button
                        colorScheme="blue"
                        onClick={handleSave}
                        isLoading={saving}
                        isDisabled={!activeValue.name.trim() || loadingLang === activeLang.code}
                    >
                        Save
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default TranslationModal;
